import { Flex, theme } from "antd";
import React, { useEffect } from "react";

import { JOB_TYPE_FILTER_MAP } from "../constants";

import { JobTag } from "./JobTag";
import { ColJobDepartment, ColJobLocation, ColJobTitle, RowJobListing } from "./styled";
import { JobListingProps } from "./types";

import { BRAND_BOOK_COLORS } from "~/Constant/Theme/configs/equipmentshare";
import { TRADE_GOTHIC_NEXT_CONDENSED_FONT_FAMILY } from "~/Constant/Theme/constants";
import { isPrerender } from "~/env";
import { useIsComponentVisible } from "~/hooks/useIsComponentVisible";
import { useOmnichannelApiSilently } from "~/OmnichannelApi/index";

export const JobListing: React.FC<JobListingProps> = ({ job, isMobileDisplay, requestId }) => {
  const { ref, isComponentVisible } = useIsComponentVisible<HTMLAnchorElement>({}, true);

  const silentApi = useOmnichannelApiSilently();

  useEffect(() => {
    if (isComponentVisible && requestId) {
      silentApi["jobs.event.create"](
        {},
        {
          request_id: requestId,
          job_id: job.google.job.name,
          event_type: "IMPRESSION",
        }
      );
    }
  }, [isComponentVisible]);

  const { token } = theme.useToken();

  const officeNames = job.google.job.customAttributes.office_names?.stringValues;

  const moreOfficesText =
    (officeNames?.length || 0) > 3 ? `+${officeNames?.slice(3).length} more locations` : undefined;

  const locations: string[] = job.google.job.derivedInfo.locations.reduce((acc, item) => {
    const str = [item?.postalAddress?.locality, item?.postalAddress?.administrativeArea].filter(Boolean).join(", ");
    if (str && !acc.includes(str)) {
      acc.push(str);
    }

    return acc;
  }, []);

  return (
    <a
      href={job.absolute_url + (!isPrerender && requestId ? `?requestId=${encodeURIComponent(requestId)}` : "")}
      target="_blank"
      style={{ color: "inherit", textDecoration: "inherit" }}
      rel="noreferrer"
      ref={ref}
    >
      <RowJobListing $token={token} className="fadeInFast">
        <ColJobTitle $token={token}>
          <div
            style={{
              lineHeight: isMobileDisplay ? "2" : "inherit",
            }}
          >
            <Flex vertical style={{ marginBottom: isMobileDisplay ? 16 : 0 }}>
              <h4
                style={{
                  fontSize: "20px",
                  marginRight: "0.5rem",
                  fontWeight: 800,
                  fontFamily: TRADE_GOTHIC_NEXT_CONDENSED_FONT_FAMILY,
                  color: BRAND_BOOK_COLORS.BLACK,
                  lineHeight: "30px",
                  textTransform: "capitalize",
                }}
              >
                {job.google.job.title}
              </h4>
              <div
                style={{
                  color: "rgba(36, 36, 45, 0.50)",
                  fontSize: "15px",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "14px",
                  letterSpacing: "0.375px",
                  marginTop: 4,
                }}
              >
                {officeNames?.slice(0, 3).join("; ")}
                {moreOfficesText && <div>{moreOfficesText}</div>}
              </div>
            </Flex>

            {isMobileDisplay && (
              <Flex vertical style={{ rowGap: 4, columnGap: 8 }}>
                <Flex wrap align="center" style={{ rowGap: 2, columnGap: 0 }}>
                  {job.google.job.customAttributes.department_names?.stringValues.map((v, idx) => (
                    <JobTag
                      key={idx}
                      color={BRAND_BOOK_COLORS.WHITE}
                      bordered={true}
                      borderColor={BRAND_BOOK_COLORS.BLACK}
                      textColor={BRAND_BOOK_COLORS.BLACK}
                    >
                      {v}
                    </JobTag>
                  ))}
                </Flex>

                <Flex wrap align="center" style={{ rowGap: 2, columnGap: 0 }}>
                  {locations[0] && (
                    <JobTag
                      color={BRAND_BOOK_COLORS.BLACK}
                      bordered={true}
                    >
                      {locations[0]}
                    </JobTag>
                  )}
                </Flex>
                {job.google.job.postingPublishTime?.seconds && (
                  <p
                    style={{
                      marginTop: 12,
                      color: "rgba(36, 36, 45, 0.50)",
                      fontSize: "12px",
                      fontWeight: 400,
                      lineHeight: "14px",
                      letterSpacing: "0.3px",
                    }}
                  >
                    Date Posted:{" "}
                    {new Date(Number(job.google.job.postingPublishTime.seconds) * 1000).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                )}
              </Flex>
            )}
          </div>
        </ColJobTitle>
        <ColJobDepartment>
          <Flex wrap align="center" style={{ rowGap: 2, columnGap: 10 }}>
            {job.google.job.customAttributes.department_names?.stringValues.map((v, idx) => (
              <JobTag
                key={idx}
                color={BRAND_BOOK_COLORS.WHITE}
                bordered={true}
                borderColor={BRAND_BOOK_COLORS.BLACK}
                textColor={BRAND_BOOK_COLORS.BLACK}
              >
                {v}
              </JobTag>
            ))}
          </Flex>
        </ColJobDepartment>
        <ColJobLocation>
          <Flex vertical>
            {locations[0] && (
              <div
                style={{
                  fontSize: "15px",
                  lineHeight: "14px",
                  letterSpacing: "0.375px",
                }}
              >
                {locations[0]}
              </div>
            )}
            <div
              style={{
                color: "rgba(36, 36, 45, 0.50)",
                fontSize: "15px",
                lineHeight: "14px",
                letterSpacing: "0.375px",
                marginTop: 6,
              }}
            >
              {job.google.job.customAttributes.work_environment?.stringValues
                ?.map((value) => JOB_TYPE_FILTER_MAP.find((entry) => entry.backend === value)?.frontend ?? value)
                .join("; ")}
            </div>
          </Flex>
        </ColJobLocation>
      </RowJobListing>
    </a>
  );
};
